import { DragDropContext,DragStart,DropResult } from "@hello-pangea/dnd";
import EditIcon from "@mui/icons-material/Edit";
import { Autocomplete,Box,Button,Divider,Stack,TextField,Typography,useTheme } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect,useRef,useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import AttToken from "../../helpers/attToken";
import { trackEventMatomo } from "../../helpers/matomo";
import Checklists from "../../service/Checklists";
import Fluxo from "../../service/Fluxo";
import FluxoRealtime from "../../service/FluxoRealtime";
import { FluxoModel,FluxoState } from "../../types/fluxo/types";
import AccomplishChecklist from "./AccomplishChecklist";

const fluxoRealtime = new FluxoRealtime();
const fluxoService = new Fluxo();
const checklistService = new Checklists();

interface Props {
  handleEditContent?: (fluxo: FluxoModel) => void;
}

const Accomplish = ({ handleEditContent }: Props) => {
  const { t } = useTranslation('translation');
  const theme = useTheme();

  const [fluxoSelecionado, setFluxoSelecionado] = useState<FluxoModel | null>(null);
  const modelos = fluxoSelecionado?.fluxo || [];

  const beckupExecucoesRef = useRef<Map<string, FluxoState>>(new Map());
  const [execucoes, setExecucoes] = useState<FluxoState[]>([]);

  const [statusById, setStatusById] = useState<Record<string, undefined | "saving" | "queuedOffline" | "error">>({});

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [itensEditing, setItensEditing] = useState<Set<string>>(new Set());
  const itensEditingRef = useRef<Set<string>>(new Set());

  // Serve apenas para estilizar o badge de itens selecionados
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const [, setHistory] = useState<{ execucoes: FluxoState[], selectedIds: Set<string>, itensEditing: Set<string> }[]>([]);

  const saveSnapshot = () => {
    setHistory(prev => [...prev, { execucoes, selectedIds, itensEditing }]);
  };

  useEffect(() => {
    itensEditingRef.current = itensEditing;
  }, [itensEditing]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'z') {
        e.preventDefault();
        setHistory(currentHisory => {
          if (currentHisory.length === 0) return currentHisory;
          const prev = currentHisory[currentHisory.length - 1];
          setExecucoes(prev.execucoes);
          setSelectedIds(prev.selectedIds);
          setItensEditing(prev.itensEditing);
          return currentHisory.slice(0, -1);
        });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  const toggleSelection = (id: string, multiSelect?: boolean) => {
    saveSnapshot();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (multiSelect) {
        next.has(id) ? next.delete(id) : next.add(id);
      }
      else {
        if(next.has(id)) next.delete(id); 
        else { return new Set([id]); }
      }
      return next;
    });
  };

  const handleDragStart = (start: DragStart) => {
    const id = start.draggableId;
    setDraggingId(id);
    if (!selectedIds.has(id)) setSelectedIds(new Set([id]));
  };

  const handleDragEnd = async (result: DropResult) => {
    saveSnapshot();
    trackEventMatomo('Acomanhamento de Fluxo', 'click', 'drag n drop', 'muda posição subitem');
    const { source, destination, draggableId } = result;
    setDraggingId(null);
    if (!destination) { toggleSelection(draggableId); return null; }
    if (source.droppableId === destination.droppableId) { toggleSelection(draggableId); return null; }

    const itemsToMove = selectedIds.has(draggableId) ? selectedIds : new Set([draggableId]);
    const idsToCancel = new Set<string>();
    const idsToMove = new Set<string>();

    //verifica se o checklist de origem é o mesmo da destino, se for, cancela a edição sem atualizar o firestore
    itemsToMove.forEach(id => {
      const originalChecklistId = beckupExecucoesRef.current.get(id)?.idChecklist;
      if (originalChecklistId === destination.droppableId) {
        idsToCancel.add(id);
      } else {
        idsToMove.add(id);
      }
    });

    if (idsToCancel.size === 0 && idsToMove.size === 0) return null;

    setExecucoes((prev) => {
      const next = [...prev];

      idsToCancel.forEach(id => {
        const original = beckupExecucoesRef.current.get(id);
        const index = next.findIndex(exec => exec.id === id);
        if (original && index !== -1) next[index] = original;
      });

      idsToMove.forEach(id => {
        const index = next.findIndex(exec => exec.id === id);
        if (index !== -1) next[index] = { ...next[index], idChecklist: destination.droppableId };
      });

      return next;
    });

    if (!navigator.onLine) setStatusById(prev => ({ ...prev, [draggableId]: 'queuedOffline' }));

    setItensEditing((prev) => {
      const newSet = new Set(prev);
      idsToCancel.forEach(id => newSet.delete(id));
      idsToMove.forEach(id => newSet.add(id));
      return newSet;
    });
  };

  const handleCancelEdit = (id: string) => {
    saveSnapshot();
    const originalExecucao = beckupExecucoesRef.current.get(id);
    if (originalExecucao) {
      setExecucoes(prev => prev.map(exec => exec.id === id ? originalExecucao : exec));
    }
    setItensEditing(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      newSet.delete(id);
      return newSet;
    });
  };

  const updateFirestore = async (id: string, droppableId: string, selectedUnidade: string | null, date: number | null) => {
    saveSnapshot();
    try {
      setStatusById(prev => ({ ...prev, [id]: 'saving' }));
      await fluxoRealtime.updateFluxoState(id, {
        idChecklist: droppableId,
        idUnidade: selectedUnidade,
        dataPrevista: date
      });
      setItensEditing(prev => {
        const remaining = new Set(prev);
        remaining.delete(id);
        return remaining;
      });
      setStatusById(prev => ({ ...prev, [id]: undefined }));
    }
    catch (error) {
      if (typeof error === 'object' && error !== null && 'code' in error && (error as { code: unknown }).code === 'unavailable') {
        return setStatusById(prev => ({ ...prev, [id]: 'queuedOffline' }));
      }
      toast.error(t('fluxo.realtime.error'), { toastId: 'errorUpdate' });
      setStatusById(prev => ({ ...prev, [id]: 'error' }));
    } finally {
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(id);
        return newSet;
      });
    }
  };

  useEffect(() => {
    const fluxoId = fluxoSelecionado?.id;
    if (!fluxoId) return;

    const unsubscribe = fluxoRealtime.subscribeToFluxo(fluxoId, (changes) => {
      setExecucoes(prev => {
        const newMap = new Map(prev.map(exec => [exec.id, exec]));

        changes.forEach(({ type, data }) => {
          if (type === 'removed') {
            newMap.delete(data.id);
            beckupExecucoesRef.current.delete(data.id);
          } else if (!itensEditingRef.current.has(data.id)) {
            newMap.set(data.id, data);
            beckupExecucoesRef.current.set(data.id, data);
          }
        });
        return Array.from(newMap.values());
      });

      setSelectedIds(prev => {
        const newSet = new Set(prev);
        changes.forEach(({ type, data }) => {
          if (type === 'removed') newSet.delete(data.id);
        });
        return newSet;
      });

      setHistory(currentHistory => {
        if (currentHistory.length === 0) return currentHistory;

        return currentHistory.map(prev => {
          const newExecucoes = [...prev.execucoes];
          const newSelectedIds = new Set(prev.selectedIds);
          const newItensEditing = new Set(prev.itensEditing);

          changes.forEach(({ data }) => {
            const index = newExecucoes.findIndex(exec => exec.id === data.id);
            if (index !== -1) {
              newExecucoes[index] = data;
            }
            newSelectedIds.delete(data.id);
            newItensEditing.delete(data.id);
          });

          return { execucoes: newExecucoes, selectedIds: newSelectedIds, itensEditing: newItensEditing };
        });
      });
    });
    return () => unsubscribe();
  }, [fluxoSelecionado?.id]);


  const { data: formulariosData } = useQuery({
    queryKey: ['GET_SIMPLE_FORMS'],
    queryFn: async () => {
      const token = await AttToken();
      if (token) {
        const response = await checklistService.getSimpleForm(token);
        return response as { id: string, nome: string }[];
      }
    },
    retry: 3,
    refetchOnWindowFocus: false
  });

  //#region: - Api que busca modelos de fluxo
  const { data: fluxoData, isLoading: loadingFluxo, isFetching: fetchingFluxo, isError: errorFluxo, isSuccess } = useQuery({
    queryKey: ['GET_FLUXOS'],
    queryFn: async () => {
      toast.info(t('fluxo.buscaFluxos'), { toastId: 'buscaFLuxos' });
      trackEventMatomo('Tela de criação e edição de fluxo', 'api busca modelos de fluxo', '', '');
      const token = await AttToken();
      if (!token) throw new Error(t('fluxo.erroBusca'));
      const response = await fluxoService.getAll(token);
      if (!response?.data.success || !Array.isArray(response.data.data)) throw new Error(t('fluxo.erroBusca'));
      return response.data.data;
    },
    retry: 3,
    refetchOnMount: 'always',
    refetchOnWindowFocus: false
  });//#endregion

  useEffect(() => {
    if (errorFluxo) {
      toast.error(t('fluxo.erroBusca'), { toastId: 'erroBusca' });
    } if (isSuccess) {
      if (fluxoData?.length) {
        toast.success(t('fluxo.modelsFind'), { toastId: 'sucessoBusca' });
      } else {
        toast.info(t('fluxo.noModels'), { toastId: 'nenhumFluxo' });
      }
    }
  }, [loadingFluxo, errorFluxo, t, fluxoData, isSuccess]);

  const getChecklistName = (id: string) => {
    return formulariosData?.find((form) => form.id === id)?.nome || 'Carregando...';
  };

  const getTotalExecucoes = (checklistId: string) => {
    return execucoes.filter(e => e.idChecklist === checklistId).length;
  };

  const disableDrop = (i: number) => {
    if (!draggingId) return false;

    const maxIndex = Array.from(selectedIds).reduce((latestId, id) => {
      const execucao = beckupExecucoesRef.current.get(id);
      const index = modelos.findIndex(m => m.checklistId === execucao?.idChecklist);
      return index > latestId ? index : latestId;
    }, -1);

    return maxIndex !== -1 && i < maxIndex;
  };

  useEffect(() => {
    // Cleans the history whenever the user selects a different Fluxo
    setHistory([]);
    setSelectedIds(new Set());
    setItensEditing(new Set());
  }, [fluxoSelecionado]);

  return (
    <Stack direction='column' spacing={2} width='100%' height='100%'>
      <Stack direction='row' justifyContent='flex-start' alignItems='center' spacing={1} width='100%'>
        <Autocomplete
          getOptionLabel={option => option.nome || ""}
          getOptionKey={option => option.id}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={fluxoSelecionado}
          disabled={
            (loadingFluxo || fetchingFluxo || errorFluxo)}
          disablePortal
          onChange={(eve, value) => {
            const selecionado = value as FluxoModel | null;
            setFluxoSelecionado(selecionado);
          }}
          options={
            fluxoData
              ?.filter((fluxo: FluxoModel) => !fluxo.dataDesativacao && fluxo.fluxo?.length > 0)
              ?.map((fluxo: FluxoModel) => ({ ...fluxo }))
            ?? []} fullWidth
          noOptionsText={t('fluxo.none')}
          renderInput={(params) => <TextField {...params} label={t('fluxo.select')} />}
        />
        {fluxoSelecionado &&
          <Button
            onClick={() => handleEditContent && handleEditContent(fluxoSelecionado)}
            sx={{ textTransform: 'none', fontWeight: 800, fontSize: 15, backgroundColor: theme.palette.primary.main }}
            variant='contained'
            endIcon={<EditIcon />}>
            {t('edit')}
          </Button>}
      </Stack>
      {fluxoSelecionado ? (
        <Box width='100%' height='100%' position='relative'>
          <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <Stack direction='row' width='100%' height='100%' divider={<Divider orientation="vertical" flexItem />} className="scroll-bar" sx={{ overflowX: 'auto' }} border={`1px solid ${theme.palette.divider}`} borderRadius={2}>
              {modelos.length > 0 ? modelos?.map((modelo, index) => (
                <AccomplishChecklist
                  key={modelo.checklistId}
                  modelo={modelo}
                  nome={getChecklistName(modelo.checklistId)}
                  execucoes={execucoes.filter(exec => exec.idChecklist === modelo.checklistId)}
                  totalExecucoes={getTotalExecucoes(modelo.checklistId)}
                  updateFirestore={updateFirestore}
                  handleCancelEdit={handleCancelEdit}
                  itensEditing={itensEditing}
                  selectedIds={selectedIds}
                  toggleSelection={toggleSelection}
                  statusById={statusById}
                  draggingId={draggingId}
                  disableDrop={disableDrop(index)}
                />
              )) : <Typography color='textDisabled' variant="h6" textAlign='center' paddingY='20%' width={'100%'}>{t('fluxo.noChecklists')}</Typography>}
            </Stack>
          </DragDropContext>
        </Box>
      ) : <Typography color='textDisabled' variant="h5" textAlign='center' paddingTop='20%'>{t('fluxo.select')}</Typography>
      }
    </Stack >
  );
};

export default Accomplish;
